import { PaneInfoRenderer, PaneInfoPaneView, PaneInfoPrimitive } from "./pane-info";
import { getOption, getColorOption } from "../Indicator";

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export class RsiPaneRenderer extends PaneInfoRenderer {
    drawRect(scope, yTop, yBottom) {
        const color = hexToRgba(this.data.colorOptions.color, 0.1);
        scope.context.beginPath();
        scope.context.rect(0, yTop, scope.mediaSize.width, yBottom - yTop);
        scope.context.fillStyle = color;
        scope.context.fill();
    }

    drawRsiFills(scope, series, rsiData, options, chart) {
        if (!rsiData || !Array.isArray(rsiData) || rsiData.length < 2) return;
        const ctx = scope.context;
        ctx.save();

        const overboughtY = series.priceToCoordinate(options.overbought);
        const oversoldY = series.priceToCoordinate(options.oversold);

        const overboughtGradient = ctx.createLinearGradient(0, overboughtY, 0, 0);
        const overboughtColor = hexToRgba(this.data.colorOptions.overboughtColor, 0.66);
        const overboughtColorFade = hexToRgba(this.data.colorOptions.overboughtColor, 0.0);
        overboughtGradient.addColorStop(0, overboughtColorFade);
        overboughtGradient.addColorStop(1, overboughtColor);

        const oversoldGradient = ctx.createLinearGradient(0, oversoldY, 0, scope.mediaSize.height);
        const oversoldColor = hexToRgba(this.data.colorOptions.oversoldColor, 0.66);
        const oversoldColorFade = hexToRgba(this.data.colorOptions.oversoldColor, 0.0);
        oversoldGradient.addColorStop(0, oversoldColorFade);
        oversoldGradient.addColorStop(1, oversoldColor);

        const points = rsiData.map((d) => {
            return {
                x: chart.timeScale().timeToCoordinate(d.time),
                y: series.priceToCoordinate(d.value),
                value: d.value,
            };
        });

        if (points.length === 0) return;

        function getIntersection(p1, p2, threshold) {
            const t = (threshold - p1.value) / (p2.value - p1.value);
            const x = p1.x + (p2.x - p1.x) * t;
            return { x, y: series.priceToCoordinate(threshold), value: threshold };
        }

        const buildSegments = (isAbove, threshold) => {
            const segments = [];
            let current = [];
            for (let i = 1; i < points.length; i++) {
                const prev = points[i - 1];
                const curr = points[i];
                const prevAbove = isAbove(prev.value, threshold);
                const currAbove = isAbove(curr.value, threshold);

                if (prevAbove) {
                    if (current.length === 0) {
                        if (!currAbove) {
                            const inter = getIntersection(prev, curr, threshold);
                            current.push(inter);
                            current.push(prev);
                            current.push(inter);
                            segments.push([...current]);
                            current = [];
                            continue;
                        } else {
                            current.push(prev);
                        }
                    }
                    current.push(curr);
                    if (!currAbove) {
                        const inter = getIntersection(prev, curr, threshold);
                        current.push(inter);
                        segments.push([...current]);
                        current = [];
                    }
                } else if (currAbove) {
                    const inter = getIntersection(prev, curr, threshold);
                    current.push(inter);
                    current.push(curr);
                }
            }
            if (current.length > 1) segments.push(current);
            return segments;
        };

        const overboughtSegments = buildSegments((v, t) => v >= t, options.overbought);
        const oversoldSegments = buildSegments((v, t) => v <= t, options.oversold);

        const drawFilledArea = (segments, threshold, gradient) => {
            if (segments.length === 0) return;
            const thresholdY = series.priceToCoordinate(threshold);
            for (const segment of segments) {
                ctx.beginPath();
                ctx.moveTo(segment[0].x, thresholdY);
                for (const pt of segment) {
                    ctx.lineTo(pt.x, pt.y);
                }
                ctx.lineTo(segment[segment.length - 1].x, thresholdY);
                ctx.closePath();
                ctx.fillStyle = gradient;
                ctx.fill();
            }
        };

        drawFilledArea(overboughtSegments, options.overbought, overboughtGradient);
        drawFilledArea(oversoldSegments, options.oversold, oversoldGradient);

        ctx.restore();
    }

    draw(target) {
        if (!this.data.visible) return;

        this.data.options = Object.fromEntries(this.data.indicators.current.get("rsi").options.map((option) => [option.id, option.value]));
        this.data.colorOptions = Object.fromEntries(this.data.indicators.current.get("rsi").colorOptions.map((option) => [option.id, option.color]));

        target.useMediaCoordinateSpace((scope) => {
            const { series, options, rsiData, getChart } = this.data;
            const yTop = series.priceToCoordinate(options.overbought);
            const yBottom = series.priceToCoordinate(options.oversold);
            const chart = getChart();

            this.drawText(scope);
            this.drawRect(scope, yTop, yBottom);
            if (rsiData) this.drawRsiFills(scope, series, rsiData, options, chart);
        });
    }
}

export class RsiPaneView extends PaneInfoPaneView {
    renderer() {
        return new RsiPaneRenderer(this._source.renderData);
    }
}

export class RsiPanePrimitive extends PaneInfoPrimitive {
    constructor({ padding = { x: 0, y: 0 }, chartType = "line", seriesRef, itemIcon, itemName, currencyIcon, activeTab, indicators }) {
        super({ padding, chartType, seriesRef, itemIcon, itemName, currencyIcon, activeTab, indicators });
        this._paneViews = [new RsiPaneView(this)];
        this.renderData.indicators = indicators;
        this.renderData.options = Object.fromEntries(indicators.current.get("rsi").options.map((option) => [option.id, option.value]));
        this.renderData.colorOptions = Object.fromEntries(indicators.current.get("rsi").colorOptions.map((option) => [option.id, option.color]));
    }

    onAttach() {
        this.renderData.series = this.series().get("mainSeries");
        this.renderData.rsiData = this.series().get("mainSeries")?.data() ?? [];
        this.renderData.rsiMAData = this.series().get("maSeries")?.data() ?? [];

        const visibleRange = this.chart()?.timeScale()?.getVisibleRange();
        const rsiInRange = this.renderData.rsiData.filter((d) => d.time >= visibleRange.from && d.time <= visibleRange.to);
        const rsiMAInRange = this.renderData.rsiMAData.filter((d) => d.time >= visibleRange.from && d.time <= visibleRange.to);

        this.update(rsiInRange[rsiInRange.length - 1], rsiMAInRange[rsiMAInRange.length - 1]);
    }

    onMouseMove = (param) => {
        const series = this.series();
        let rsi, rsiMA;
        if (param) {
            rsi = param.seriesData.get(series?.get("mainSeries"));
            rsiMA = param.seriesData.get(series?.get("maSeries"));
        }
        if (rsi || rsiMA) {
            this.update(rsi, rsiMA);
        }
    };

    update(rsi, rsiMA) {
        let value = rsi?.value;
        let period = getOption(this.renderData.indicators.current.get("rsi"), "period").value || 14;
        let content = [];
        if (value !== undefined) {
            content.push({ text: `RSI ${period} ` });
            content.push({ text: `${value.toFixed(2)} `, color: this.renderData.colorOptions.color });
            content.push({ text: `${rsiMA?.value.toFixed(2)} `, color: this.renderData.colorOptions.maColor });
            if (value > (this.renderData.options.overbought ?? 70))
                content.push({ text: "Overbought", color: this.renderData.colorOptions.overboughtColor });
            else if (value < (this.renderData.options.oversold ?? 30))
                content.push({ text: "Oversold", color: this.renderData.colorOptions.oversoldColor });
            else content.push({ text: "Neutral", color: "#aaa" });
        }
        this.updateContent(content);
    }
}
